import type {FC} from 'react'
import {useEffect, useMemo, useState} from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'

import {
  useDeleteMessageFeedbackMeMutation,
  useUpsertMessageFeedbackMeMutation,
} from '@apis/aiCoreAPI'
import type {MessageFeedbackRating} from '@interfaces/alpinist/messageFeedback'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import type {SxProps, Theme} from '@mui/material/styles'

interface Props {
  conversationId: string
  messageId: string
  disabled?: boolean
  initialRating?: MessageFeedbackRating | null
  initialDetails?: string | null
}

const MAX_DETAILS_LEN = 2000
const ICON_BOX_SIZE = 34

const MessageFeedbackActions: FC<Props> = ({
  conversationId,
  messageId,
  disabled,
  initialRating,
  initialDetails,
}) => {
  const [t] = useCustomTranslation('alpinist')

  const [upsertFeedback, {isLoading: isSaving}] = useUpsertMessageFeedbackMeMutation()
  const [deleteFeedback, {isLoading: isDeleting}] = useDeleteMessageFeedbackMeMutation()

  const [selected, setSelected] = useState<MessageFeedbackRating | null>(initialRating ?? null)
  const [details, setDetails] = useState<string>(initialDetails ?? '')
  const [pendingRating, setPendingRating] = useState<MessageFeedbackRating | null>(null)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const popOpen = Boolean(anchorEl)
  const openDetails = (el: HTMLElement) => setAnchorEl(el)
  const closeDetails = () => setAnchorEl(null)

  useEffect(() => {
    setSelected(initialRating ?? null)
    setDetails(initialDetails ?? '')
    setPendingRating(null)
  }, [messageId, initialRating, initialDetails])

  const busy = disabled || isSaving || isDeleting

  const canSave = useMemo(() => {
    if (!selected) return false
    if (details.length > MAX_DETAILS_LEN) return false
    return true
  }, [selected, details])

  const iconWrapSx: SxProps<Theme> = {
    width: ICON_BOX_SIZE,
    height: ICON_BOX_SIZE,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  }

  const isSpinning = (r: MessageFeedbackRating) => busy && pendingRating === r

  const toggleRating = async (rating: MessageFeedbackRating, el?: HTMLElement) => {
    if (busy) return
    setPendingRating(rating)

    if (selected === rating) {
      setSelected(null)
      setDetails('')
      closeDetails()
      try {
        await deleteFeedback({conversationId, messageId}).unwrap()
      } finally {
        setPendingRating(null)
      }
      return
    }

    setSelected(rating)
    if (el) openDetails(el)

    try {
      const res = await upsertFeedback({
        conversationId,
        messageId,
        rating,
        details: details ? details : undefined,
      }).unwrap()

      if (res?.rating) setSelected(res.rating)
      if (typeof res?.details !== 'undefined') setDetails(res.details ?? '')
    } finally {
      setPendingRating(null)
    }
  }

  const handleSaveDetails = async () => {
    if (!selected || busy) return
    setPendingRating(selected)

    try {
      const res = await upsertFeedback({
        conversationId,
        messageId,
        rating: selected,
        details: details ? details : undefined,
      }).unwrap()

      if (res?.rating) setSelected(res.rating)
      if (typeof res?.details !== 'undefined') setDetails(res.details ?? '')
      closeDetails()
    } finally {
      setPendingRating(null)
    }
  }

  const savingDetails = busy && pendingRating === selected

  return (
    <Box
      sx={{
        mt: 0.75,
        pt: 0.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.25}}>
        <Typography variant="caption" color="text.secondary">
          {t('message_feedback.label')}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="flex-start">
        <Tooltip title={t('message_feedback.thumbs_down')}>
          <Box component="span" sx={iconWrapSx}>
            <IconButton
              size="small"
              disabled={busy}
              onClick={(e) => toggleRating('dislike', e.currentTarget)}
              aria-label={t('message_feedback.thumbs_down')}
            >
              {selected === 'dislike' ? (
                <ThumbDownAltIcon fontSize="small" />
              ) : (
                <ThumbDownAltOutlinedIcon fontSize="small" />
              )}
            </IconButton>
            {isSpinning('dislike') ? (
              <CircularProgress size={16} sx={{position: 'absolute'}} />
            ) : null}
          </Box>
        </Tooltip>

        <Tooltip title={t('message_feedback.thumbs_up')}>
          <Box component="span" sx={iconWrapSx}>
            <IconButton
              size="small"
              disabled={busy}
              onClick={(e) => toggleRating('like', e.currentTarget)}
              aria-label={t('message_feedback.thumbs_up')}
            >
              {selected === 'like' ? (
                <ThumbUpAltIcon fontSize="small" />
              ) : (
                <ThumbUpAltOutlinedIcon fontSize="small" />
              )}
            </IconButton>
            {isSpinning('like') ? <CircularProgress size={16} sx={{position: 'absolute'}} /> : null}
          </Box>
        </Tooltip>

        <Tooltip title={t('message_feedback.love')}>
          <Box component="span" sx={iconWrapSx}>
            <IconButton
              size="small"
              disabled={busy}
              onClick={(e) => toggleRating('love', e.currentTarget)}
              aria-label={t('message_feedback.love')}
            >
              {selected === 'love' ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
            {isSpinning('love') ? <CircularProgress size={16} sx={{position: 'absolute'}} /> : null}
          </Box>
        </Tooltip>

        {selected ? (
          <Button
            size="small"
            disabled={busy}
            onClick={(e) => openDetails(e.currentTarget)}
            sx={{minWidth: 0, px: 1, ml: 0.25}}
          >
            {t('message_feedback.details')}
          </Button>
        ) : null}
      </Stack>

      <Popover
        open={popOpen}
        anchorEl={anchorEl}
        onClose={closeDetails}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        slotProps={{paper: {sx: {p: 1.25, width: 360, maxWidth: '90vw'}}}}
      >
        <TextField
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          value={details}
          disabled={busy}
          onChange={(e) => setDetails(e.target.value)}
          placeholder={t('message_feedback.details_placeholder')}
          helperText={`${details.length}/${MAX_DETAILS_LEN}`}
          error={details.length > MAX_DETAILS_LEN}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{mt: 1}}>
          <Button size="small" onClick={closeDetails} disabled={busy}>
            {t('message_feedback.close')}
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleSaveDetails}
            disabled={!canSave || busy}
            startIcon={savingDetails ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {savingDetails ? t('message_feedback.saving') : t('message_feedback.save')}
          </Button>
        </Stack>
      </Popover>
    </Box>
  )
}

export default MessageFeedbackActions
